import React, {useEffect, useRef, useState, ReactNode} from 'react';
import {Comparator, comparators, comparatorsField, comparatorsPred, funcOrValOr, objFilter, pickKeys} from '../tools/CommonJS';
import {classNameScrollbar, classNameScrollbarThin, LoadingAnim, MkSvg} from '../tools/CommonHtml';
import {componentInit, tailCheck, useRerender, useStateEx} from '../tools/CommonReact';
import {Button, Checkbox} from '../tools/Inputs';
import {UIVirtualTable} from '../tools/UIVirtualTable';
import {useSidePanel} from '../tools/UIPopSidePanel';
import {Divider, DividerListButtons, Title} from './CommonUIComponenets';
import {appCtx} from './AppContext';
import {icn} from '../tools/UIIcons';
import {ListItem} from './UIDataComponents';

export enum listSelectModes {
	all            = 'multi-select, buttons: select-all, none.',
	reversible     = 'multi-select, buttons: select-all, none. select-all triggers isSelRev.',
	none           = 'multi-select, buttons: none.',
	noHeader       = 'multi-select, no total or buttons.',
	single         = 'single-select, buttons: none.',
	noSelect       = 'no selection at all.'
}

export type ListSelectMode = (typeof listSelectModes)[keyof typeof listSelectModes];


export interface SelectableState<T> {
	list?: T[];
	selectMode?: ListSelectMode;
	selector?: SelectorFunctions<T>;
	sels?: Set<any>;
	selsVal?: Map<any, any>;                                           // Identity key -> action value, when selector splits the two (see listSelsKey valKey).
	selsByDev?: Set<string>;
	selsById?: Set<number>;
	selsByGuid?: Set<string>;
	selsByParam?: Record<string, [string, string]>;
	isSelRev?: boolean;
	clear?: () => void;
	numSelected?: () => number;
	isAllSelected?: () => boolean;
	isSelected?: (params: {rowData: T; index?: number}) => boolean;
	getSelection?: () => any;                                           // note: ignores isSelRev
	getSelItems?: () => T[];                                            // note: ignores isSelRev
	getSelItemsIndex?: () => {rowData: T; index: number}[];             // note: ignores isSelRev
	selectAllNone?: (params: {isAll: boolean; predSelectable?: (rowData: T) => boolean; list?: T[]}) => void;
	toggle?: (params: {rowData?: T; index?: number; to?: boolean; predSelectable?: (rowData: T) => boolean}) => void;
}

interface UseListSelectableParams<T> {
	oSels?: SelectableState<T>;
	list?: T[];
	header?: (props: ListHeaderColumnsProps<T> | ListHeaderTotalsProps<T>) => ReactNode;
	styleHeight?: React.CSSProperties;
	className?: string;
	classNameHeight?: string;
	classNameListItem?: string | ((args: {rowData: T; index: number}) => string | undefined);
	SidePanel?: React.ComponentType<any>;
	sidePanelClassName?: string;
	sidePanelProps?: any;
	emptyMsg?: string;
	onClick?: (params: {rowData: T; index: number}) => void | unknown;
	dispRowDetails?: (rowData: T, index: number) => ReactNode;
	onPaging?: (params: any) => void;
	handleClr?: string | ((rowData: T) => string);
	leftTip?: string | ((rowData: T) => string);
	noSrvCache?: boolean;
	error?: any;
	rowDataTestPred?: (rowData: T) => string;
	predDrawCheckbox?: (rowData: T) => boolean;
	predClickRow?: (rowData: T) => boolean;
	onSidePanel?: (params: {rowData?: T; index?: number}) => void;
	nonVirtual?: boolean;
	triggerOpenIdx?: number;
	isLoading?: boolean;
	addButtons?: ReactNode;
	dataTest?: string;
	predRowActive?: (rowData: T) => boolean;
	overflowX?: boolean;            // Default: true. Wraps list in overflow-x-auto so columns scroll horizontally when they can't fit.
}

interface ListHeaderTotalsProps<T> {
	isLoading?: boolean;
	list?: T[];
	addButtons?: ReactNode;
	oSels?: SelectableState<T>;
	showSelsOnly?: boolean;
	setShowSels?: (toState: boolean) => ((e: any) => void) | undefined;
	selectAllNone?: (isAll: boolean) => void;
}

type ListColCbArgs<T, P extends object = Record<string, any>> = {
	value: unknown;
	rowData: T;
	className?: string;
} & Partial<P>;

type ColWFlag = 'flex' | 'rem' | 'fixOrder' | 'fixWidth' | 'minTitle';

export interface ListColDef<T, P extends object = Record<string, any>> {
	k?: Extract<keyof T, string>;
	h: string;                                  // column title (always string)
	hEx?: ReactNode;                            // if set, replaces h visually in the header
	hTip?: ReactNode;                           // tooltip content shown via ? icon next to header
	defaultSort?: boolean;                  // if true, this column will be sorted by default.
	dsc?: boolean;                          // boolean if by default to sort Descending. otherwise Ascending. If not specified, column is not sortable.
	noOtherDir?: boolean;                   // if true, this column will not be sortable in other direction than the one specified by dsc.
	cmpr?: Comparator<any>;                 // in case you're using listSort
	className?: string;
	classNameCell?: string | ((_: ListColCbArgs<T, P>) => string | undefined);
	classNameHeader?: string;                   // extra classes on this column's header cell. Table-edge inset on reorderable UIListTable: use classNameHeader + classNameRow on the table, not here.
	classNameTitle?: string | ((_: ListColCbArgs<T, P>) => string | undefined);
	tip?: (_: ListColCbArgs<T, P>) => string;  // tooltip text
	prep?: (_: ListColCbArgs<T, P>) => any;    // pre-process a value before presenting it or passing to other callbacks
	d?: (_: ListColCbArgs<T, P>) => any;       // override value presentation
	dataTest?: string;
	w?: number;                                 // width: flex-grow for "flex", rem for "rem"
	// space-separated flags (default "rem")
	wType?: ColWFlag | `${ColWFlag} ${ColWFlag}` | `${ColWFlag} ${ColWFlag} ${ColWFlag}` | `${ColWFlag} ${ColWFlag} ${ColWFlag} ${ColWFlag}`;
	cellStyle?: React.CSSProperties;            // inline style for cells
}

/**
 * Column def for tab/table configs (`TableCol`, `objToArrK`): widened `h` so titles may be `ReactNode`.
 * Every `ListColDef` satisfies this type (structural widen of `h`).
 */
export type ListColDefTab<T = any, P extends object = Record<string, any>> = Omit<ListColDef<T, P>, 'h'> & {
	h?: string | ReactNode;
};

// helper utility to define colDefs object.
// e.g.: const colsDefs: ListColDefObj<TRspRec, "adhocCol1" | "adhocCol2", ExtraProps> = {...}
export type ListColDefObj<
	Row,
	ExtraKeys extends string = never,
	P extends object = Record<string, any>
> = Partial<
	Record<Extract<keyof Row, string> | ExtraKeys, ListColDef<Row, P>>
>;

export type ListColSort<T, P extends object = Record<string, any>> = Pick<ListColDef<T, P>, 'k' | 'dsc'>;

export type ListSortDef<T> = Pick<ListColDef<T>, 'k' | 'dsc' | 'defaultSort' | 'prep'> & {
	cmpr?: Comparator<any, any[]>;
};

export type ListSortDefObj<Row, ExtraKeys extends string = never> =
	Partial<Record<Extract<keyof Row, string> | ExtraKeys, ListSortDef<Row>>>;

interface ListHeaderColumnsProps<T, P extends object = Record<string, any>> {
	cols?: ListColDefTab<T, P>[];
	className?: string;
	sortBy?: ListColSort<T>;
	onSort?: (params: {k: Extract<keyof T, string>; dsc: boolean}) => void;
	handleSortCol?: ListColSort<T>;     // Sort definition for the row handle (rendered before columns).
	list?: T[];
	oSels?: SelectableState<T>;
	showSelsOnly?: boolean;
	selectAllNone?: (isAll: boolean) => void;
	addButtons?: ReactNode;
	dispTotal?: (n: number) => any;
	addRowProps?: P;
	rowPropsProcessor?: (params: {rowData: T & Partial<P>}) => P;               // allows processing addRowProps props before passing to row renderer.
	sclBarPx?: number;
}

export interface SelectorFunctions<T> {
	clear: (params: {oSels: SelectableState<T>}) => void;
	numSelected: (params: {oSels: SelectableState<T>}) => number;
	isSelected: (params: {rowData?: T; index?: number; oSels: SelectableState<T>}) => boolean;
	toggle: (params: {rowData?: T; index?: number; to?: boolean; oSels: SelectableState<T>}) => void;
	getSelection: (params: {oSels: SelectableState<T>}) => any;
	getSelItemsIndex: (params: {oSels: SelectableState<T>; list?: T[]}) => {rowData: T; index: number}[];
	getSelItems: (params: {oSels: SelectableState<T>; list?: T[]}) => T[];
	skimSels?: (params: {oSels: SelectableState<T>; list?: T[]}) => void;
	makeKey?: (rowData: T) => any;
}


export function useListSelectable<T>(
		{
			oSels, list, header, styleHeight, className, classNameHeight = 'h-[10rem]', classNameListItem,
			SidePanel, sidePanelClassName, sidePanelProps,
			emptyMsg, onClick, dispRowDetails, onPaging, handleClr, leftTip,
			noSrvCache, error, rowDataTestPred, predRowActive,
			predDrawCheckbox, predClickRow, onSidePanel, nonVirtual, triggerOpenIdx,
			isLoading, addButtons,        // passed to header. can be passed directly instead.
			dataTest = 'list-selectable',
			overflowX = true,
		}: UseListSelectableParams<T>) {
	const rerender = useRerender();
	const vals = useRef<{showSelsOnly: boolean; panelId?: string; rowData?: T; sized?: boolean; _prevList?: any; sclBarPx?: number}>({showSelsOnly: false}).current;
	const {
		setPanel, panelsJsx, closePanel, isPanelOn
	} = SidePanel? useSidePanel() : {};
	if (vals._prevList !== list && vals._prevList?.length !== list?.length) { vals._prevList = list; vals.sized = false; }
	header ??= (prov: ListHeaderColumnsProps<T>) => <ListHeaderTotals {...prov}/>;

	function selectAllNone(isAll: boolean) {
		oSels?.selectAllNone?.({isAll, predSelectable: predDrawCheckbox, list});
		rerender();
	}

	const onClickHandler = (params: {rowData: T; index: number}) => onClick? (() => onClick(params)) : SidePanel && (() => openSidePanel(params));

	useEffect(() => {
		if (!(triggerOpenIdx >= 0)) return;
		onClickHandler({rowData: list![triggerOpenIdx], index: triggerOpenIdx})?.();
	}, [triggerOpenIdx]);

	function closeSidePanel() {
		if (!isPanelOn?.(vals.panelId)) return;
		onSidePanel?.({});
		closePanel?.(vals.panelId);
		delete vals.rowData;
	}

	function openSidePanel({rowData, index}: {rowData: T; index: number}) {
		if (isPanelOn?.(vals.panelId) && vals.rowData === rowData)
			return closeSidePanel();
		vals.rowData = rowData;
		onSidePanel?.({rowData, index});
		vals.panelId = setPanel?.({
			Elm: SidePanel,
			panelId: vals.panelId,
			obj: rowData,
			triggerOpenIdx,
			onClose: () => onSidePanel?.({}),
			...funcOrValOr(sidePanelProps) as any,
			className: `bg-bg2 w-[29rem] h-[100vh] top-0 right-0 p-4 rounded-l-xl shadow-2xl ${sidePanelClassName}`
		});
	}

	// returns an onClick function only if current state is not already desired state.
	const setShowSels = (toState: boolean) => vals.showSelsOnly !== toState && (() => ((vals.showSelsOnly = toState), rerender()));

	function ShowSelecteds() {
		const nSels = oSels?.numSelected?.() || 0;
		const items = oSels?.getSelItemsIndex?.() || [];
		items.sort(comparatorsField(comparators.numberSig, 'index'));

		return <div data-test='list-selectable' className='flex flex-col gap-2 h-full'>
			{!nSels? 'No items selected.' : nSels === list?.length && oSels?.isSelRev? 'All items selected.' : <>
				{oSels?.isSelRev && 'All except:'}
				{nonVirtual?
					<div>{items.map(({rowData, index}, actlIndex) =>
						rowRenderer({rowData, index, actlIndex}))}</div> :
					<UIVirtualTable
						rows={items}
						className={`grow overflow-hidden ${classNameHeight}`}
						rowRenderer={({rowData: {rowData, index}, index: actlIndex}: any) => rowRenderer({rowData, index, actlIndex})}
						styleHeight={styleHeight}/>}
			</>}
		</div>;
	}

	function rowRenderer({rowData, index, actlIndex}: {rowData: T; index: number; actlIndex?: number}) {
		const isSelected = oSels?.isSelected?.({rowData, index});
		return <ListItem
			data-test={rowDataTestPred ? rowDataTestPred(rowData) : `${index}-row-renderer-list-item`}
			key={actlIndex ?? index} className={tailCheck(funcOrValOr(classNameListItem, {rowData, index}), 'mr-1')}
			onClick={(predClickRow?.(rowData) ?? true) && onClickHandler({rowData, index}) || undefined}
			active={vals.rowData === rowData && isPanelOn?.(vals.panelId) || predRowActive?.(rowData)}
			handleClr={funcOrValOr(handleClr, rowData) ?? (isSelected ? 'bg-prm-light' : undefined)}
			leftTip={funcOrValOr(leftTip, rowData)}>
			{oSels && oSels.selectMode !== listSelectModes.noSelect &&
				<div className='shrink-0 w-5 h-full'>
					{!!(predDrawCheckbox?.(rowData) ?? true) &&
						<Checkbox
							className='h-full'
							isChecked={isSelected}
							onChange={() => {
								oSels.toggle?.({rowData, index});
								rerender();
							}}
							field=''/>}
				</div>}
			{dispRowDetails?.(rowData, index)}
		</ListItem>;
	}

	const listContent =
		<div data-test='list-selectable-container' className={tailCheck(className, 'flex flex-col h-full', 'gap-2', overflowX && 'min-w-[50rem]')}>
			{oSels?.selectMode !== listSelectModes.noHeader &&
				header({list, oSels, showSelsOnly: vals.showSelsOnly, setShowSels, selectAllNone, isLoading, addButtons, sclBarPx: vals.sclBarPx})}
			{vals.showSelsOnly?
				<ShowSelecteds/> :
				!list? (
					<div className='flex justify-center items-center h-full'>
						<LoadingAnim {...{noSrvCache, error}} classNameSize='w-10 h-10' reload={appCtx.nav.reload}/>
					</div>
				):
					!list.length?
						<div data-test='list-selectable-empty-msg' className='ml-8 text-tx-steel'>{emptyMsg ?? '(None)'}</div> :
						nonVirtual?
							<div className={`overflow-auto ${classNameHeight} ${classNameScrollbar}`}>
								{list.map((rowData, index) => rowRenderer({rowData, index}))}
							</div>
							:
							<UIVirtualTable
								rows={list} dataTest={dataTest}
								className={`grow pr-1 overflow-hidden ${classNameHeight}`}
								rowRenderer={rowRenderer}
								onSizing={p => {
									onPaging?.(p);
									if (!vals.sized || vals.sclBarPx !== p.sclBarPx) { vals.sized = true; vals.sclBarPx = p.sclBarPx; rerender(); }
								}}
								styleHeight={styleHeight}/>}
			{panelsJsx}
		</div>;

	return {
		openSidePanel,
		closeSidePanel,
		listJsx: overflowX
			? <div className={`overflow-x-auto h-full ${classNameScrollbarThin}`}>{listContent}</div>
			: listContent,
	};
}

export const ListHeaderTotals = <T, >({
	addButtons, isLoading, list, oSels, showSelsOnly, setShowSels, selectAllNone
}: ListHeaderTotalsProps<T>) => (
	<div className='shrink-0 items-center flex text-sm border-handle gap-3 mr-1.5 mb-1'>
		{!!list?.length &&
			<Button
				data-test='list-selectable-total' h={<><span className='opacity-80'>Total:&nbsp;</span>{list?.length.toLocaleString()}</>}
				className={showSelsOnly && 'text-prm-light'}
				onClick={oSels && oSels.selectMode !== listSelectModes.noSelect && setShowSels?.(false)}/>}
		{!!list?.length && oSels && oSels.selectMode !== listSelectModes.noSelect && <>
			<DividerListButtons/>
			<Button
				data-test='list-selectable-selected-count' h={<>{oSels.numSelected?.().toLocaleString()}<span className='opacity-80'>&nbsp;selected</span></>}
				className={!showSelsOnly && 'text-prm-light'} onClick={setShowSels?.(true)}/>
			{[listSelectModes.all, listSelectModes.reversible].includes(oSels.selectMode!) && <>
				<DividerListButtons/>
				<Button data-test='list-selectable-select-all' h='Select all' className='text-sm text-prm-light' onClick={() => selectAllNone?.(true)}/>
			</>}
			<DividerListButtons/>
			<Button data-test='list-selectable-select-none' h='none' className='text-sm text-prm-light' onClick={() => selectAllNone?.(false)}/>
		</>}
		{list && isLoading && <div className='grow flex justify-end mr-2'><LoadingAnim classNameSize='w-5 h-5'/></div>}
		{addButtons}
	</div>
);


export const genColumns = <T, P extends object = Record<string, any>>(
	{
		cols, className = '', sortBy, onSort, handleSortCol, dispTotal, addButtons, addRowProps, rowPropsProcessor
	}: ListHeaderColumnsProps<T, P>) => ({
	header: props => (
		<ListHeaderColumns {...{
			cols,
			className,
			sortBy,
			onSort,
			handleSortCol,
			...props,
			dispTotal: dispTotal ?? props.dispTotal,
			addButtons: addButtons ?? props.addButtons,
		}}/>
	),
	dispRowDetails: (rowData, index) => {
		const processedProps = rowPropsProcessor?.({rowData, index, ...addRowProps}) ?? addRowProps;
		return cols.map(col => listColumnRow(col, rowData, processedProps));
	},
});

export const ListHeaderColumns = <T, >(
	{
		cols, className = '', sortBy, onSort, handleSortCol, list, oSels, selectAllNone, addButtons, dispTotal, sclBarPx
	}: ListHeaderColumnsProps<T>) => {
	const isChecked = oSels?.isAllSelected?.() || false;
	const isHandleActive = handleSortCol && sortBy?.k === handleSortCol.k;
	return <div className='flex flex-col gap-4 overflow-hidden shrink-0 min-h-0'>
		{(oSels || dispTotal || addButtons) &&
			<div className='flex items-center gap-3'>
				{(oSels || dispTotal) &&
					<div className='text-xs text-subtitle1'>
						{oSels && oSels.selectMode !== listSelectModes.noSelect
							? <>Selected: <span data-test='list-selectable-selected-count'>{oSels.numSelected().toLocaleString()}</span> / </>
							: 'Showing: '}
						<span data-test='list-selectable-total'>
							{dispTotal
								? dispTotal(list?.length) ?? ''
								: list?.length.toLocaleString() ?? ''}
						</span>
					</div>}
				{addButtons}
			</div>}
		<ListItem className={tailCheck(className, 'mr-', 'bg- h- mb-', 'text-xs')} style={sclBarPx ? {marginRight: sclBarPx + 'px'} : undefined} handleClr='bg-transparent' noHandle={!!handleSortCol}>
			{handleSortCol &&
				<Button
					className='shrink-0 w-2 mr- flex items-center group/handle'
					data-test={`sortBy-${handleSortCol.k}-handle`}
					onClick={() => onSort?.({k: handleSortCol.k, dsc: isHandleActive ? !sortBy.dsc : handleSortCol.dsc})}>
					<div className={`${!isHandleActive && 'opacity-0 group-hover/handle:opacity-70 transition-opacity'}`}>
						{icn.sortDir(isHandleActive ? sortBy.dsc : handleSortCol.dsc)}</div>
				</Button>}
			{oSels && oSels.selectMode !== listSelectModes.noSelect &&
				<div className='shrink-0 w-5 h-full' data-test='list-selectable-select-all-checkbox'>
					<Checkbox className='h-full' isChecked={isChecked} onChange={() => selectAllNone?.(!isChecked)}/>
				</div>}
			{cols?.map(({
				k, h, hEx, className, dsc, noOtherDir
			}, idx) => (
				<Button
					key={idx} className={`flex items-center mr- group/col ${className}`}
					data-test={`sortBy-${k}${dsc ? '-dsc' : '-asc'}`}
					onClick={typeof dsc === 'boolean' && (sortBy?.k !== k || !noOtherDir) &&
							(() => onSort?.({k, dsc: sortBy?.k === k? !sortBy.dsc : dsc}))}>
					<div>{hEx ?? h}</div>
					{typeof dsc === 'boolean' && <div className={`${sortBy?.k !== k && 'opacity-0 group-hover/col:opacity-70 transition-opacity'}`}>
						{icn.sortDir(sortBy?.k === k? sortBy.dsc : dsc)}</div>}
				</Button>
			))}
		</ListItem>
	</div>;

};

export function getDefaultSort<T>(cols: ListColDefTab<T>[], addSortKeys?: ListSortDef<T>[]): ListColSort<T> {
	const all = addSortKeys ? [...addSortKeys, ...cols] : cols;
	const col = all.find(c => c.defaultSort) ?? all.find(c => c.dsc !== undefined);
	return col && pickKeys(col, 'k', 'dsc');
}

export function listColumnRow<T>(col: ListColDefTab<T>, rowData: T, addProps?: object) {
	let value = rowData[col.k] as any;
	const arg: ListColCbArgs<T> = {value, rowData, className: col.className, ...addProps};
	if (col.prep)
		value = col.prep(arg);
	if (col.d)
		value = col.d({...arg, value});

	return <div
		key={col.k}
		className={`flex ${col.className} ${funcOrValOr(col.classNameCell, arg)} relative`}
		title={funcOrValOr(col.tip, arg) || undefined} data-test={col.dataTest}>
		{typeof value === 'object'? value :
			<Title children={value} className='max-w-full'/>}
	</div>;
}

export function listSort<T extends Array<any>>(
		{
			list, defs, sortBy, dfltBy, addSortKeys
		}:
		{list?: T, defs, sortBy, dfltBy, addSortKeys?: ListSortDefObj<T[number]>}
): T | undefined {
	list = list?.slice() as T;
	defs = {...defs, ...addSortKeys};
	if (typeof sortBy === 'string')                                 // Legacy bare-key form from older persisted URL state.
		sortBy = {k: sortBy, dsc: defs[sortBy]?.dsc};
	if (!defs[sortBy.k])
		sortBy = {k: dfltBy, dsc: defs[dfltBy]?.dsc};
	if (sortBy.k !== dfltBy)
		list?.sort(comparatorsField(defs[dfltBy].cmpr, dfltBy));
	return list?.sort(!defs[sortBy.k].prep
		? comparatorsField(defs[sortBy.k].cmpr, sortBy.k, !!sortBy.dsc)
		: comparatorsPred(defs[sortBy.k].cmpr, row => defs[sortBy.k].prep({value: row[sortBy.k], rowData: row}), !!sortBy.dsc));
}


export const SidePanelContainer = ({onClose, title, children}) => (
	<div data-test='side-panel-container' className='flex flex-col h-full gap-2 overflow-y-scroll scrollbar-none'>
		<div data-test='side-panel-container-headers' className='flex justify-between'>
			<Title className='py-2 text-xl grow'>{title}</Title>
			<Button data-test='side-panel-container-close' onClick={onClose} className='text-tx-cool-grey -mr-1'><MkSvg className='h-7 w-7' strokeWidth='2' d='M6 18L18 6M6 6l12 12'/></Button>
		</div>
		<Divider classNameColor='bg-bg4 mb-2 shrink-0'/>
		{children}
	</div>
);


export const listSelsIndexes: SelectorFunctions<any> = {
	clear: ({oSels}) => oSels.sels = new Set(),
	numSelected: ({oSels}) => oSels.sels.size,
	isSelected: ({index, oSels}) => oSels.sels.has(index) || false,
	toggle: ({oSels, to, index}) => oSels.sels.toggle(index, to),
	getSelection: ({oSels}) => oSels.sels,
	getSelItemsIndex: ({oSels, list}) => [...oSels.sels].map(index => ({rowData: list?.[index] ?? index, index})),
	getSelItems: ({oSels, list}) => [...oSels.sels].map(index => list?.[index]).filter(Boolean),
	skimSels: ({oSels, list}) => oSels.sels = new Set([...oSels.sels].filter(i => list?.[i])),
};

export const listSelsDevice: SelectorFunctions<any> = {
	clear: ({oSels}) => Object.assign(oSels, {selsByDev: new Set(), selsById: new Set(), selsByGuid: new Set()}),
	numSelected: ({oSels}) => (oSels.selsByDev?.size || 0) + (oSels.selsById?.size || 0) + (oSels.selsByGuid?.size || 0),
	isSelected: ({rowData, oSels}) =>
		oSels.selsByDev?.has(rowData.device) ||
		oSels.selsById?.has(rowData.deviceId) ||
		oSels.selsByGuid?.has(rowData.deviceGuid) ||
		false,
	toggle: ({rowData, to, oSels}) =>
		rowData.deviceId? oSels.selsById?.toggle(rowData.deviceId, to) :
			rowData.deviceGuid? oSels.selsByGuid?.toggle(rowData.deviceGuid, to) :
				oSels.selsByDev?.toggle(rowData.device, to),
	getSelection: ({oSels}) => ({
		list: [...(oSels.selsByDev || [])],
		listIds: [...(oSels.selsById || [])],
		listGuids: [...(oSels.selsByGuid || [])],
	}),
	getSelItemsIndex: ({oSels, list}) => {
		const itmKey = (k: string) => (v: any) => {
			let index = list?.findIndex(e => e?.[k] === v);
			if (!(index >= 0)) index = undefined;
			return ({rowData: list?.[index] ?? {[k]: v}, index});
		};
		return [
			...[...(oSels.selsByDev || [])].map(itmKey('device')),
			...[...(oSels.selsById || [])].map(itmKey('deviceId')),
			...[...(oSels.selsByGuid || [])].map(itmKey('deviceGuid'))
		];
	},
	getSelItems: ({oSels, list}) => list?.filter(e =>
		oSels.selsByDev?.has(e?.device) || oSels.selsById?.has(e?.deviceId) || oSels.selsByGuid?.has(e?.deviceGuid)) ?? [],
	skimSels: ({oSels, list}) => Object.assign(oSels, {
		selsByDev: new Set([...(oSels.selsByDev ?? [])].filter(k => list?.some(d => d?.device === k))),
		selsById: new Set([...(oSels.selsById ?? [])].filter(k => list?.some(d => d?.deviceId === k))),
		selsByGuid: new Set([...(oSels.selsByGuid ?? [])].filter(k => list?.some(d => d?.deviceGuid === k)))
	}),
};

export const listSelsParam: SelectorFunctions<any> = {
	clear: ({oSels}) => oSels.selsByParam = {},
	numSelected: ({oSels}) => Object.keys(oSels.selsByParam || {}).length,
	isSelected: ({rowData, oSels}) => !!oSels.selsByParam?.[`${rowData.param}_*_${rowData.paramExtra}`],
	toggle: ({rowData, to, oSels}) => {
		if (to === false || oSels.selsByParam?.[`${rowData.param}_*_${rowData.paramExtra}`])
			delete oSels.selsByParam[`${rowData.param}_*_${rowData.paramExtra}`];
		else
			oSels.selsByParam[`${rowData.param}_*_${rowData.paramExtra}`] = [rowData.param, rowData.paramExtra];
	},
	getSelection: ({oSels}) => ({
		list: Object.values(oSels.selsByParam || {}).map(v => v[0]),
		listParamExtra: Object.values(oSels.selsByParam || {}).map(v => v[1]),
	}),
	getSelItemsIndex: ({oSels, list}) => Object.values(oSels.selsByParam || {}).map(v => {
		let index = list?.findIndex(e => e?.param === v[0] && e?.paramExtra === v[1]);
		if (!(index >= 0)) index = undefined;
		return ({rowData: list?.[index] ?? {param: v[0], paramExtra: v[1]}, index});
	}),
	getSelItems: ({oSels, list}) => Object.values(oSels.selsByParam || {}).map(v =>
		list?.find(e => e?.param === v[0] && e?.paramExtra === v[1])).filter(Boolean),
	skimSels: ({oSels, list}) => oSels.selsByParam = objFilter(oSels.selsByParam || {},
																																																												(_k, v) => list?.some(e => e?.param === v[0] && e?.paramExtra === v[1])),
};

// key identifies a row (for highlight/toggle); pass valKey when the action value differs from the identity
// (e.g. patches: identity is topicCode+version so duplicate topicCodes select independently, but actions run by topicCode).
export const listSelsKey = <T, >(key: keyof T | ((e: T) => any), valKey?: keyof T | ((e: T) => any)): SelectorFunctions<T> => {
	const makeVal = valKey === undefined ? undefined
		: typeof valKey === 'function'? (rowData: T) => valKey(rowData) : (rowData: T) => rowData[valKey];
	return {
		clear: ({oSels}) => {oSels.sels = new Set(); if (makeVal) oSels.selsVal = new Map();},
		makeKey: typeof key === 'function'? rowData => key(rowData) : rowData => rowData[key],
		numSelected: ({oSels}) => oSels.sels.size || 0,
		isSelected: ({rowData, oSels}) => oSels.sels.has(oSels.selector.makeKey(rowData)),
		toggle: ({rowData, to, oSels}) => {
			const k = oSels.selector.makeKey(rowData);
			const removed = oSels.sels.delete(k);
			if (removed) oSels.selsVal?.delete(k);
			if (to === true || (!removed && to !== false)) {
				if (oSels.selectMode === listSelectModes.single)
					oSels.clear();
				oSels.sels.add(k);
				if (makeVal) oSels.selsVal?.set(k, makeVal(rowData));
			}
		},
		getSelection: ({oSels}) => makeVal ? [...new Set(oSels.selsVal?.values() ?? [])] : [...oSels.sels],
		getSelItemsIndex: ({oSels, list}) => [...oSels.sels]
			.map(k => {
				const index = list?.findIndex(e => oSels.selector.makeKey(e) === k);
				return ({rowData: list?.[index], index});
			})
			.filter(e => e.rowData),
		getSelItems: ({oSels, list}) => list?.filter(e => oSels.sels.has(oSels.selector.makeKey(e))) ?? [],
		skimSels: ({oSels, list}) => {
			oSels.sels = new Set([...oSels.sels].filter(k => list?.some(d => oSels.selector.makeKey(d) === k)));
			if (oSels.selsVal) oSels.selsVal = new Map([...oSels.selsVal].filter(([k]) => oSels.sels.has(k)));
		},
	};
};

interface UseSelectableParams<T> {
	selector?: SelectorFunctions<T> | ((key: any) => SelectorFunctions<T>);
	key?:  keyof T | ((e: T) => any);
	selectMode?: ListSelectMode;
	list?: T[];
	deps?: any[];
}

export function useSelectable<T>({
	selector = listSelsIndexes, key, selectMode, list, deps = []
}: UseSelectableParams<T>): [SelectableState<T>] {
	const {state: oSels} = useStateEx<SelectableState<T>>({});
	Object.assign(oSels, {list, selectMode});

	if (componentInit(oSels, [selector, key], '_init_selector'))
		oSels.selector = funcOrValOr(selector, key);

	if (componentInit(oSels)) {
		oSels.clear = () => {
			oSels.selector.clear({oSels});
			oSels.isSelRev = false;
		};

		oSels.numSelected = () => {
			const nSels = oSels.selector.numSelected({oSels});
			return oSels.isSelRev? (oSels.list?.length - nSels) || 0 : nSels;
		};

		oSels.isAllSelected = () => !!oSels.list?.length && oSels.numSelected() === oSels.list?.length;

		oSels.isSelected = ({rowData, index}) => !oSels.selector.isSelected({rowData, index, oSels}) !== !oSels.isSelRev;

		oSels.getSelection = () => oSels.selector.getSelection({oSels});                                // note: ignores isSelRev
		oSels.getSelItems = () => oSels.selector.getSelItems({oSels, list: oSels.list});                // note: ignores isSelRev
		oSels.getSelItemsIndex = () => oSels.selector.getSelItemsIndex({oSels, list: oSels.list});      // note: ignores isSelRev

		oSels.selectAllNone = ({isAll, predSelectable, list}) => {
			if (list && list !== oSels.list)
				return list?.forEach((rowData, index) => oSels.toggle({rowData, index, to: !!isAll, predSelectable}));

			oSels.selector.clear({oSels});
			if (oSels.selectMode === listSelectModes.reversible)
				oSels.isSelRev = !!isAll;
			else if (isAll)
				oSels.list?.forEach((rowData, index) => oSels.toggle({rowData, index, to: true, predSelectable}));
		};

		oSels.toggle = ({
			rowData, index, to, predSelectable
		}) =>
			(predSelectable?.(rowData) ?? true) &&
			oSels.selector.toggle({rowData, index, to, oSels});
	}

	// by default, selection is not cleared when changed - only skimmed to include retained items.
	// If you need to clear selections on list change, add list to passed deps.
	if (componentInit(oSels, [list, ...deps], '_init_skim'))
		if (componentInit(oSels, deps, '_init_clear') || oSels.isSelRev)
			oSels.clear?.();
		else if (list)
			oSels.selector.skimSels?.({oSels, list});


	return [oSels];
}

export const ListTotals = ({list, oSels, dispTotal}: {list?: any[], oSels?: SelectableState<any>, dispTotal?: (n: number) => any}) => (
	<div className='text-xs text-subtitle1'>
		{oSels && oSels.selectMode !== listSelectModes.noSelect
			? <>Selected: <span data-test='list-selectable-selected-count'>{oSels.numSelected().toLocaleString()}</span> / </>
			: 'Showing: '}
		<span data-test='list-selectable-total'>
			{dispTotal
				? dispTotal(list?.length) ?? ''
				: list?.length?.toLocaleString() ?? ''}
		</span>
	</div>
);
